import { useId } from 'react';
import { Link } from 'react-router';
import type { EventType, EventView, RsvpAnswer } from '@grc/shared';
import { t, type Code } from '../../i18n';
import { dayHeading, startTime, type EventDay } from './dates';

// The events' rows, a day at a time under its heading (B3; D34), for the Events tab's lists and the
// month view's day: each row the time on the club's clock, the title (the link to its page), the
// type and the place, and the answers: the yes and maybe counts with the member's own answer, or,
// for a cancelled event, only that it's cancelled (the owner's answer, 4A).

const TYPES: Record<EventType, Code> = {
  training: 'event.training',
  race: 'event.race',
  social: 'event.social',
};

const SAID: Record<RsvpAnswer, Code> = {
  yes: 'events.saidYes',
  maybe: 'events.saidMaybe',
  no: 'events.saidNo',
};

/** An event's type, in words: Training, Race or Social. */
export function typeText(type: EventType): string {
  return t(TYPES[type]);
}

/** "Training · Atatürk Park", or the type alone when it has no place. */
export function typePlace(event: Pick<EventView, 'type' | 'place'>): string {
  const type = typeText(event.type);
  return event.place === null ? type : t('events.typePlace', { type, place: event.place });
}

/** What a row's link leaves in the event page's history entry: the view it was opened from. */
export type FromState = { from: string };

/**
 * The days, each under its heading (h2 in the lists, h3 under the month's grid), each event a row
 * whose link remembers `from`, the view's path, for the page's way back.
 */
export function EventDays({
  days,
  today,
  from,
  level = 2,
}: {
  days: readonly EventDay[];
  today: string;
  from: string;
  level?: 2 | 3;
}) {
  return days.map((day) => (
    <DaySection key={day.date} day={day} today={today} from={from} level={level} />
  ));
}

function DaySection({
  day,
  today,
  from,
  level,
}: {
  day: EventDay;
  today: string;
  from: string;
  level: 2 | 3;
}) {
  const headingId = useId();
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <section className="event-day" aria-labelledby={headingId}>
      <Heading id={headingId}>{dayHeading(day.date, today)}</Heading>
      <ul className="event-list">
        {day.events.map((event) => (
          <EventRow key={event.id} event={event} from={from} />
        ))}
      </ul>
    </section>
  );
}

function EventRow({ event, from }: { event: EventView; from: string }) {
  const cancelled = event.status === 'cancelled';
  const state: FromState = { from };
  return (
    <li className={cancelled ? 'event-row event-row-cancelled' : 'event-row'}>
      <span className="event-row-time">{startTime(event)}</span>
      <span className="event-row-body">
        {/* The link's name is the event's title; the whole row is its tap target (styles.css). */}
        <Link
          className="event-row-link"
          to={`/events/${encodeURIComponent(event.id)}`}
          state={state}
        >
          {event.title}
        </Link>
        <span className="event-row-meta">{typePlace(event)}</span>
        <span className="event-row-answers">
          {cancelled ? (
            <span className="tag tag-cancelled">{t('events.cancelled')}</span>
          ) : (
            <>
              <span>
                {t('events.counts', { yes: event.counts.yes, maybe: event.counts.maybe })}
              </span>
              {event.myAnswer !== null && (
                <span className="tag tag-answer">{t(SAID[event.myAnswer])}</span>
              )}
            </>
          )}
        </span>
      </span>
    </li>
  );
}
